import { View, Text } from 'react-native';
import { useEffect, useRef, useState } from 'react';
import { usePathname, useRouter } from 'expo-router';
import { trpc } from '../../lib/trpc';
import { useBudgetRegenerate } from '../../hooks/useBudgetRegenerate';
import { buildBudgetDisplayCategories } from '../../lib/budget-display';
import { groupAndSortCategories } from '../../lib/category-config';
import { trackOnboardingStepCompleted } from '../../lib/product-events';
import {
  OnboardingShell,
  Button,
  Card,
  ProgressBar,
  LoadingSpinner,
  BudgetCategoryGroups,
  WebHead,
} from '../../components';
import { formatCurrency } from '../../lib/utils';

export default function Step5Budget() {
  const router = useRouter();
  const pathname = usePathname();
  const advanceMutation = trpc.households.updateOnboardingStep.useMutation();

  const averagesQ = trpc.budget.averages.useQuery(undefined, {
    refetchOnMount: 'always',
  });
  const entriesQ = trpc.budget.getEntries.useQuery(undefined, {
    refetchOnMount: 'always',
  });
  const budgetsQ = trpc.budget.list.useQuery(undefined, {
    refetchOnMount: 'always',
  });
  const budgetCategoriesQ = trpc.budget.listPrimaryCategories.useQuery(undefined, {
    refetchOnMount: 'always',
  });
  const cashflowQ = trpc.budget.annualizedCashflow.useQuery(undefined, {
    refetchOnMount: 'always',
  });
  const updateEntryMutation = trpc.budget.updateEntry.useMutation();
  const createEntryMutation = trpc.budget.createEntry.useMutation();
  const createBudgetMutation = trpc.budget.create.useMutation();
  const autoGenerationStartedRef = useRef(false);

  const averages = averagesQ.data ?? [];
  const entries = entriesQ.data ?? [];
  const cashflow = cashflowQ.data;
  const [error, setError] = useState<string | null>(null);
  const [budgetDrafts, setBudgetDrafts] = useState<Record<string, string>>({});
  const [savingCategoryId, setSavingCategoryId] = useState<string | null>(null);
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(null);
  const [rowErrorCategoryId, setRowErrorCategoryId] = useState<string | null>(null);
  const [rowErrorMessage, setRowErrorMessage] = useState<string | null>(null);
  const [expandedGroupId, setExpandedGroupId] = useState<string | null>(null);

  const handleToggleGroup = (groupId: string) => {
    setExpandedGroupId((current) => (current === groupId ? null : groupId));
  };
  const {
    isRegenerating,
    regenerateError,
    regenerateResult,
    runBudgetRegenerate,
  } = useBudgetRegenerate({
    onSuccess: () =>
      Promise.allSettled([
        averagesQ.refetch(),
        entriesQ.refetch(),
        cashflowQ.refetch(),
      ]),
  });

  useEffect(() => {
    if (averages.length === 0) return;
    setBudgetDrafts((prev) => {
      let next = prev;
      let changed = false;

      for (const avg of averages) {
        const categoryId = avg.budgetCategoryId;
        const entry = entries.find((e) => e.budgetCategoryId === categoryId && !e.isDerived);
        const draftKey = entry?.entryKey ?? `average-${categoryId}`;
        if (categoryId == null || editingCategoryId === draftKey) continue;

        const amount = entry ? Number(entry.amount) : Number(avg.monthlyAverage);
        const nextValue = String(Math.round(amount * 100) / 100);

        if ((entry || prev[draftKey] === undefined) && prev[draftKey] !== nextValue) {
          if (!changed) {
            next = { ...prev };
            changed = true;
          }
          next[draftKey] = nextValue;
        }
      }

      return changed ? next : prev;
    });
  }, [averages, editingCategoryId, entries]);

  useEffect(() => {
    if (autoGenerationStartedRef.current) return;
    if (averagesQ.isLoading || entriesQ.isLoading) return;
    if (entries.length > 0) return;

    autoGenerationStartedRef.current = true;
    void runBudgetRegenerate();
  }, [averagesQ.isLoading, entries.length, entriesQ.isLoading, runBudgetRegenerate]);

  const budgetCategories = buildBudgetDisplayCategories(
    averages,
    entries,
    budgetCategoriesQ.data ?? [],
  );
  const groupedBudgetCategories = groupAndSortCategories(
    budgetCategories.map((category) => {
      const budgeted = Number(category.entryAmount ?? category.monthlyAverage);
      const actual = Number(category.monthlyAverage);
      const suggestedBudget = Number(
        category.entryAmount ?? category.monthlyAverage,
      );
      const ratio = budgeted > 0 ? actual / budgeted : 0;
      const overBudget = budgeted > 0 && actual > budgeted;

      return {
        ...category,
        budgeted,
        actual,
        suggestedBudget,
        ratio,
        overBudget,
      };
    }),
  );

  useEffect(() => {
    if (groupedBudgetCategories.length > 0 && expandedGroupId === null) {
      setExpandedGroupId(groupedBudgetCategories[0].plaidPrimary);
    }
  }, [groupedBudgetCategories, expandedGroupId]);

  const totalMonthlyBudget = budgetCategories.reduce(
    (sum, category) => sum + Number(category.entryAmount ?? category.monthlyAverage),
    0,
  );
  const customizedCategoryCount = entries.length;
  const overBudgetCategoryCount = budgetCategories.filter((category) => {
    const budgeted = Number(category.entryAmount ?? category.monthlyAverage);
    const actual = Number(category.monthlyAverage);
    return budgeted > 0 && actual > budgeted;
  }).length;

  const ensureActiveBudgetId = async () => {
    let budgetId = entries[0]?.budgetId;
    if (!budgetId) {
      const loadedBudgets = budgetsQ.data ?? (await budgetsQ.refetch()).data ?? [];
      const existingBudget = loadedBudgets.find((b) => b.isActive) ?? loadedBudgets[0];
      budgetId = existingBudget?.id;
    }

    if (!budgetId) {
      await createBudgetMutation.mutateAsync({
        name: 'My Budget',
        isActive: true,
      });
      const refreshedBudgets = (await budgetsQ.refetch()).data ?? [];
      const createdOrExistingBudget =
        refreshedBudgets.find((b) => b.isActive) ?? refreshedBudgets[0];
      budgetId = createdOrExistingBudget?.id;
    }

    return budgetId;
  };

  const handleSaveBudget = async (displayId: string) => {
    setError(null);
    setRowErrorCategoryId(null);
    setRowErrorMessage(null);
    const category = budgetCategories.find((entry) => entry.displayId === displayId);
    if (!category || category.isEditable === false) {
      setRowErrorCategoryId(displayId);
      setRowErrorMessage('This amount is derived from a liability and must be updated there.');
      return;
    }
    const budgetCategoryId = category.budgetCategoryId;
    const parsedAmount = parseFloat(budgetDrafts[displayId] ?? '');
    if (Number.isNaN(parsedAmount) || parsedAmount < 0) {
      setRowErrorCategoryId(displayId);
      setRowErrorMessage('Please enter a valid monthly budget amount.');
      return;
    }

    try {
      setSavingCategoryId(displayId);
      const budgetId = await ensureActiveBudgetId();
      if (!budgetId) {
        setRowErrorCategoryId(displayId);
        setRowErrorMessage('Could not create a budget to update.');
        return;
      }

      const existingEntry = entries.find((e) => e.budgetCategoryId === budgetCategoryId && !e.isDerived);
      if (existingEntry) {
        await updateEntryMutation.mutateAsync({
          budgetId,
          budgetCategoryId,
          amount: parsedAmount,
        });
      } else {
        await createEntryMutation.mutateAsync({
          budgetId,
          budgetCategoryId,
          amount: parsedAmount,
        });
      }
      await Promise.allSettled([entriesQ.refetch(), cashflowQ.refetch()]);
      setEditingCategoryId(null);
      setRowErrorCategoryId(null);
      setRowErrorMessage(null);
    } catch {
      setRowErrorCategoryId(displayId);
      setRowErrorMessage('Could not update this budget category. Please try again.');
    } finally {
      setSavingCategoryId(null);
    }
  };

  const handleContinue = async () => {
    await advanceMutation.mutateAsync({ step: 6 });
    trackOnboardingStepCompleted({
      route: pathname,
      stepNumber: 5,
      nextStepNumber: 6,
      completionKind: 'continue',
      metadata: {
        budgetCategoryCount: budgetCategories.length,
        customizedCategoryCount,
        overBudgetCategoryCount,
        totalMonthlyBudget: Math.round(totalMonthlyBudget),
      },
    });
    router.replace('/(onboarding)/step-6-family' as any);
  };

  const isInitializingBudget =
    entries.length === 0
    && autoGenerationStartedRef.current
    && isRegenerating;
  const hasActiveBudgetEditor = editingCategoryId !== null;

  if (averagesQ.isLoading || isInitializingBudget) {
    return (
      <>
      <OnboardingShell
        step={4}
        title="Review Your Budget"
        subtitle="We’re turning your spending into a first-draft monthly budget."
        footer={
          <Button disabled loading={isRegenerating || averagesQ.isLoading}>
            Building your first-draft budget…
          </Button>
        }
      >
        <WebHead><title>Step 5: Budget Review – Per Diem</title></WebHead>
        <Card className="items-center py-8">
          <LoadingSpinner
            fullScreen={false}
            message="Reviewing recent spending, recurring charges, refunds, and likely one-time expenses."
          />
        </Card>
      </OnboardingShell>
      </>
    );
  }

  return (
    <>
    <OnboardingShell
      step={4}
      title="Review Your Budget"
      subtitle="Based on your transactions, here's where your money goes. You can fine-tune this later."
      footer={
        <Button
          testID="onboarding-budget-continue-button"
          accessibilityLabel="Continue from budget review"
          onPress={handleContinue}
          loading={advanceMutation.isPending}
        >
          Budget looks right →
        </Button>
      }
    >
      <WebHead><title>Step 5: Budget Review – Per Diem</title></WebHead>
      <View className="gap-4">
        {(error ?? regenerateError) && (
          <View
            className="rounded-xl bg-red-50 px-4 py-3"
            onStartShouldSetResponder={() => hasActiveBudgetEditor}
          >
            <Text className="text-sm text-red-600">{error ?? regenerateError}</Text>
          </View>
        )}
        {regenerateResult && (
          <View
            className="rounded-xl bg-green-50 px-4 py-3"
            onStartShouldSetResponder={() => hasActiveBudgetEditor}
          >
            <Text className="text-sm text-green-700">{regenerateResult}</Text>
          </View>
        )}
        {cashflow && (
          <View onStartShouldSetResponder={() => hasActiveBudgetEditor}>
          <Card>
            <View className="flex-row justify-between mb-1">
              <Text className="text-sm text-gray-600">Monthly Income</Text>
              <Text className="text-sm font-semibold text-green-600">
                {formatCurrency(Number(cashflow.annualIncome) / 12)}
              </Text>
            </View>
            <View className="flex-row justify-between mb-3">
              <Text className="text-sm text-gray-600">Monthly Budget</Text>
              <Text className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                {formatCurrency(totalMonthlyBudget)}
              </Text>
            </View>
            <ProgressBar
              value={
                Number(cashflow.annualIncome) > 0
                  ? (totalMonthlyBudget * 12) / Number(cashflow.annualIncome)
                  : 0
              }
            />
          </Card>
          </View>
        )}

        {groupedBudgetCategories.length > 0 && (
          <>
            <Text className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              First-Draft Budget by Category
            </Text>
            <BudgetCategoryGroups
              groups={groupedBudgetCategories}
              expandedGroupId={expandedGroupId}
              onToggleGroup={handleToggleGroup}
              editingCategoryId={editingCategoryId}
              getDraftValue={(categoryId) => budgetDrafts[categoryId] ?? ""}
              onDraftChange={(categoryId, text) => {
                setBudgetDrafts((prev) => ({
                  ...prev,
                  [categoryId]: text,
                }));
                setError(null);
                if (rowErrorCategoryId === categoryId) {
                  setRowErrorCategoryId(null);
                  setRowErrorMessage(null);
                }
              }}
              onSave={handleSaveBudget}
              onStartEditing={(categoryId, currentAmount) => {
                setEditingCategoryId(categoryId);
                setBudgetDrafts((prev) => ({
                  ...prev,
                  [categoryId]: String(Math.round(currentAmount * 100) / 100),
                }));
                setError(null);
                setRowErrorCategoryId(null);
                setRowErrorMessage(null);
              }}
              onCancelEditing={() => {
                setEditingCategoryId(null);
                setRowErrorCategoryId(null);
                setRowErrorMessage(null);
              }}
              savingCategoryId={savingCategoryId}
              getRowErrorMessage={(categoryId) =>
                rowErrorCategoryId === categoryId ? rowErrorMessage : null
              }
              showTransactionLinks={false}
            />
          </>
        )}

        {groupedBudgetCategories.length === 0 && (
          <View onStartShouldSetResponder={() => hasActiveBudgetEditor}>
          <Card className="items-center py-6">
            <Text className="text-sm text-gray-500 text-center">
              Not enough transaction data yet. Connect accounts and wait for the initial sync.
            </Text>
          </Card>
          </View>
        )}
      </View>
    </OnboardingShell>
    </>
  );
}
